/**
 * The platform-neutral Live Talk controller. Browser ownership only:
 * admission, provider identity and charges come from the host/World. Both
 * the Desk (apps/web) and Pocket (apps/mobile) fold over this one module.
 *
 * No DOM, no React, no `structuredClone` (Hermes lacks it), no `crypto`:
 * identifiers and media come through `LiveDependencies`.
 */
import {
  livePriorDialogueRows,
  utf8ByteLength,
  type LiveClientDelegation,
  type LivePriorDialogueRow,
  type LiveTranscriptFragment,
} from "./liveDelegationProtocol.ts";

export type LiveAgentReference = {
  agentKey: string;
  revision: number;
} & ({ mode: "test"; activationRevision?: never } | { mode: "live"; activationRevision: number });
export type LiveAgentSnapshot = LiveAgentReference & {
  configDigest: string;
  name: string;
  purpose: string;
  style: string;
  voice: string;
  backendMode: "caller";
  backendProvider: null;
  backendModel: null;
};
/** The default published voice; the World's quote names the full list. */
export const LIVE_DEFAULT_VOICE = "marin";
export const validLiveVoiceName = (value: unknown): value is string =>
  typeof value === "string" && /^[A-Za-z][A-Za-z0-9_-]{0,31}$/.test(value);
export function validLiveAgentReference(value: unknown): value is LiveAgentReference {
  if (!value || typeof value !== "object") return false;
  const a = value as LiveAgentReference;
  return (
    /^VA_[A-Za-z0-9_-]{1,93}$/.test(a.agentKey) &&
    Number.isSafeInteger(a.revision) &&
    a.revision > 0 &&
    ((a.mode === "test" && a.activationRevision === undefined) ||
      (a.mode === "live" && Number.isSafeInteger(a.activationRevision) && a.activationRevision > 0))
  );
}
export const liveAgentReference = (agent: LiveAgentReference): LiveAgentReference =>
  agent.mode === "live"
    ? {
        agentKey: agent.agentKey,
        revision: agent.revision,
        mode: "live",
        activationRevision: agent.activationRevision,
      }
    : { agentKey: agent.agentKey, revision: agent.revision, mode: "test" };
export function matchesLiveAgent(
  reference: LiveAgentReference | undefined,
  value: unknown,
): value is LiveAgentSnapshot | undefined {
  if (!reference) return value === undefined;
  if (!validLiveAgentReference(value)) return false;
  const a = value as LiveAgentSnapshot;
  return (
    a.agentKey === reference.agentKey &&
    a.revision === reference.revision &&
    a.mode === reference.mode &&
    a.activationRevision === reference.activationRevision &&
    /^[a-f0-9]{64}$/.test(a.configDigest) &&
    typeof a.name === "string" &&
    !!a.name.trim() &&
    a.name.length <= 80 &&
    typeof a.purpose === "string" &&
    !!a.purpose.trim() &&
    a.purpose.length <= 4000 &&
    typeof a.style === "string" &&
    !!a.style.trim() &&
    a.style.length <= 1000 &&
    validLiveVoiceName(a.voice) &&
    a.backendMode === "caller" &&
    a.backendProvider === null &&
    a.backendModel === null
  );
}
/** Browser ownership only. Admission, provider identity and charges come from the host. */
export interface LiveScope {
  worldKey: string;
  expectedActorId: string;
  conversation: { roomId: string; sessionId: string; environmentId: string; threadId: string };
  backend: { instanceId: string; model: string };
  agent?: LiveAgentReference;
}
export interface LivePrice {
  revision: string;
  currency: "USD";
  rateMicrosPerMinute: number;
}
export interface LivePrivacy {
  version: "live-privacy-v1";
  revision: number;
  recording: "off";
  taskContext: "spoken-request-only" | "supporting-dialogue";
  /** The saved voice choice, once the World publishes it on the privacy record. */
  voice?: string;
}
/** The saved voice choice: its own revision, distinct from the privacy revision. */
export interface LiveVoiceChoice {
  revision: number;
  voice: string;
  version?: string;
  voicesRevision?: string;
  supported?: boolean;
}
export interface LivePrivacyRead {
  ok: true;
  worldKey: string;
  actorId: string;
  privacy: LivePrivacy;
  voice?: LiveVoiceChoice;
}
export function validLiveVoiceChoice(value: unknown): value is LiveVoiceChoice {
  if (!value || typeof value !== "object") return false;
  const c = value as LiveVoiceChoice;
  return Number.isSafeInteger(c.revision) && c.revision >= 0 && validLiveVoiceName(c.voice);
}
export function validLivePrivacy(value: unknown): value is LivePrivacy {
  if (!value || typeof value !== "object") return false;
  const p = value as LivePrivacy;
  return (
    p.version === "live-privacy-v1" &&
    Number.isSafeInteger(p.revision) &&
    p.revision >= 0 &&
    p.recording === "off" &&
    (p.taskContext === "spoken-request-only" || p.taskContext === "supporting-dialogue") &&
    (p.voice === undefined || validLiveVoiceName(p.voice))
  );
}
export interface LiveQuote {
  ok: true;
  worldKey: string;
  actorId: string;
  conversation: LiveScope["conversation"];
  price: LivePrice;
  maximumVoiceChargeMicros: number;
  privacy: LivePrivacy;
  agent?: LiveAgentSnapshot;
  /** The seconds this quote priced (the agreed limit requested). */
  maximumSeconds?: number;
  /** The World's published voice list at a named revision, when it publishes one. */
  voices?: { revision: string; choices: string[] };
  /** The saved voice choice, or the default. */
  voice?: string;
  /** Funded credit currently available to this member, in micro-USD. */
  availableCreditMicros?: number;
  /** The World's suggested agreed limit for this member. */
  agreedMaximumSecondsDefault?: number;
}
export interface LiveCall {
  providerStorage?: {
    requested: "off" | "unknown";
    observed: "off" | "on" | "conflicting" | "unknown";
  };
  privacy: LivePrivacy | null;
  requestKey: string;
  callKey: string;
  agent?: LiveAgentSnapshot;
  status: "reserved" | "running" | "stopping" | "unconfirmed" | "settled" | "released";
  backend: LiveScope["backend"];
  model: string;
  voice: string;
  price: LivePrice;
  maximumChargeMicros: number;
  chargeMicros: number;
  releasedMicros: number;
  transportAnswer: null | { sdp: string; providerSessionId: string; expiresAt: number };
  /** The World's reservation expiry; the client's deadline is derived from this, never a local constant. */
  expiresAt?: number;
  /** The funded total after extensions. */
  maximumSeconds?: number;
  /** Extensions the World has granted for this call. */
  extensions?: number;
  /** The trusted observer's failure, when the call did not reach or keep a provider session. */
  observation?: { failure?: string; [key: string]: unknown } | null;
}
export function validLiveProviderStorage(
  value: unknown,
): value is NonNullable<LiveCall["providerStorage"]> {
  if (!value || typeof value !== "object") return false;
  const report = value as NonNullable<LiveCall["providerStorage"]>;
  return (
    (report.requested === "off" || report.requested === "unknown") &&
    (report.observed === "off" ||
      report.observed === "on" ||
      report.observed === "conflicting" ||
      report.observed === "unknown")
  );
}
export interface LiveRead {
  ok: true;
  worldKey: string;
  actorId: string;
  conversation: LiveScope["conversation"];
  call: LiveCall | null;
  admission?: "not-dispatched";
  requestKey?: string;
  reason?: string;
  refusalReceipt?: { kind: "live-admission-refusal-v1"; requestKey: string; createdAt: number };
}
export interface LiveMediaEndDetail {
  /** The only end the controller may recover from on its own; every other end carries no detail. */
  reason: "connection-lost";
}
export interface LiveMedia {
  offer(): Promise<string>;
  answer(sdp: string, providerSessionId: string): Promise<void>;
  close(): void;
  sendUpdate?(update: LiveUpdate): void;
  /** True once every captured and received track has ended and playback is detached. */
  released?(): boolean;
}
export interface LiveUpdate {
  kind: "thinking" | "commentary";
  delegationId: string | null;
  content: string;
  eventId: string;
}
export type LiveConversationEvent = {
  privacy: LivePrivacy | null;
  requestKey: string;
  scope: LiveScope;
} & (
  | { kind: "transcript"; fragment: LiveTranscriptFragment }
  | { kind: "delegation"; delegation: LiveClientDelegation }
  /** The replacement connection is live; its dialogue continues the dropped connection's. */
  | { kind: "replacement"; previousRequestKey: string }
);
/** The host certifies no local dispatch, or verifies a durable World admission refusal. */
export class LiveStartNotDispatched extends Error {
  readonly requestKey: string;
  constructor(message: string, requestKey: string) {
    super(message);
    this.name = "LiveStartNotDispatched";
    this.requestKey = requestKey;
  }
}
export type LiveRequestOperation =
  | "quote"
  | "start"
  | "extend"
  | "read"
  | "stop"
  | "privacy"
  | "privacy-set"
  | "voice-set";
/** Time and timers are injected: the shared controller owns no platform globals. */
export interface LiveClock {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}
export interface LiveDependencies {
  clock: LiveClock;
  request(
    operation: LiveRequestOperation,
    body: Record<string, unknown>,
  ): Promise<LiveQuote | LiveRead | LivePrivacyRead>;
  media(
    onReady: () => void,
    onEnd: (error?: string, detail?: LiveMediaEndDetail) => void,
    callbacks?: {
      onTranscript?(fragment: LiveTranscriptFragment): void;
      onDelegation?(delegation: LiveClientDelegation): void;
    },
  ): LiveMedia;
  uuid(): string;
  /**
   * LVC1: save the dropped call's rows into the thread before a replacement
   * starts, so the World seeds the replacement from the written conversation.
   * Resolves false (or throws) when nothing could be saved; the controller
   * then carries the dialogue itself as bounded memory.
   */
  flushTranscript?(requestKey: string): Promise<boolean>;
}
export type LivePhase =
  | "off"
  | "quoting"
  | "quoted"
  | "starting"
  | "connecting"
  | "live"
  | "reconnecting"
  | "stopping"
  | "unconfirmed"
  | "ended"
  | "error";
export interface LiveState {
  privacyPending: LivePrivacy["taskContext"] | null;
  privacySaving: boolean;
  privacyRetry: boolean;
  voicePending: string | null;
  voiceSaving: boolean;
  voiceRetry: boolean;
  phase: LivePhase;
  quote: LiveQuote | null;
  call: LiveCall | null;
  error: string | null;
  /** The person's agreed limit for one conversation of calls, in seconds. */
  agreedMaximumSeconds: number;
  /** The funded total of the current call after extensions, when one is running. */
  fundedSeconds: number | null;
  /** The World's expiry read back, less its close margin; null until read back. */
  deadlineAt: number | null;
  extensions: number;
  /** An extension the World refused; the call continues to its funded limit. */
  extensionRefusal: string | null;
  /** An admission or provider refusal shown as-is: no retry loop, no fallback. */
  refusal: string | null;
  /** The dropped call this call replaces, when a connection was recovered. */
  replacementOf: string | null;
  /** Microphone, playback and received tracks released, or still held. */
  device: "released" | "held";
}
interface Attempt {
  privacy: LivePrivacy;
  scope: LiveScope;
  agent?: LiveAgentSnapshot;
  requestKey: string;
  voice: string;
  agreed: number;
  maximumSeconds: number;
  cancelled: boolean;
  sent: boolean;
  startPending: boolean;
  media: LiveMedia;
  answerApplied: boolean;
  timer?: unknown;
  deadline?: unknown;
  extendTimer?: unknown;
  reconcileUntil: number;
  failures: number;
  deadlineAt: number | null;
  extensions: number;
  extending: boolean;
  extensionRefused: boolean;
  /** The connection was lost while live and the person did not End: recover once. */
  dropped: boolean;
  replacementOf: string | null;
  dialogue: LiveTranscriptFragment[];
  dialogueBytes: number;
  /** The price the person started this call at; a replacement never starts at another (Can 68). */
  priceRevision: string;
}
/** The World's hard cap on one funded call. */
export const LIVE_MAXIMUM_SECONDS = 300;
/** Agreed-limit choices offered in the panel. */
export const LIVE_LIMIT_CHOICES: readonly number[] = [60, 120, 180, 300];
/** The first funded segment; later segments arrive through `extend`. */
export const LIVE_INITIAL_SEGMENT_SECONDS = 60;
export const LIVE_EXTENSION_STEP_SECONDS = 60;
/** Ask for the next segment this long before the current deadline. */
export const LIVE_EXTENSION_LEAD_MS = 20_000;
/** The World's observer begins closing this long before reservation expiry. */
export const LIVE_WORLD_CLOSE_MARGIN_MS = 10_000;
/** No automatic replacement when less than this remains and nothing can be extended. */
const RECOVERY_MINIMUM_REMAINING_MS = 5_000;
/** How long a call is reconciled after its funded deadline before its outcome is called unconfirmed. */
const RECONCILE_WINDOW_MS = 150_000;
const DIALOGUE_MAX_FRAGMENTS = 2_048;
const DIALOGUE_MAX_BYTES = 256_000;

const jsonClone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const requestScope = ({ worldKey, expectedActorId, conversation }: LiveScope) => ({
  worldKey,
  expectedActorId,
  conversation,
});
const identity = (scope: LiveScope | null) => JSON.stringify(scope);
const terminal = (call: LiveCall | null) =>
  call?.status === "settled" || call?.status === "released";
const message = (error: unknown) =>
  error instanceof Error ? error.message : "Live connection failed.";
const matches = (scope: LiveScope, value: LiveQuote | LiveRead) =>
  value.ok === true &&
  value.worldKey === scope.worldKey &&
  value.actorId === scope.expectedActorId &&
  Object.entries(scope.conversation).every(
    ([key, v]) => value.conversation?.[key as keyof LiveScope["conversation"]] === v,
  );
/** The trusted observer's failure, worded for the panel. Never a retry, never a fallback. */
export function liveRefusalText(call: LiveCall | null | undefined): string | null {
  const failure = call?.observation?.failure;
  if (typeof failure !== "string" || !failure) return null;
  if (failure === "provider-create-refused")
    return "The voice provider refused to create this call. No other provider or mode was used; the reservation is released.";
  if (failure === "provider-create-unconfirmed")
    return "Provider session creation is unconfirmed. The World is still reconciling this call; nothing else was started.";
  return `Live call failed: ${failure}. No fallback was used.`;
}
const validQuoteExtras = (quote: LiveQuote) =>
  (quote.maximumSeconds === undefined ||
    (Number.isSafeInteger(quote.maximumSeconds) && quote.maximumSeconds > 0)) &&
  (quote.voices === undefined ||
    (!!quote.voices &&
      typeof quote.voices === "object" &&
      typeof quote.voices.revision === "string" &&
      quote.voices.revision.length > 0 &&
      quote.voices.revision.length <= 128 &&
      Array.isArray(quote.voices.choices) &&
      quote.voices.choices.length > 0 &&
      quote.voices.choices.length <= 64 &&
      quote.voices.choices.every(validLiveVoiceName))) &&
  (quote.voice === undefined ||
    (validLiveVoiceName(quote.voice) &&
      (quote.voices === undefined || quote.voices.choices.includes(quote.voice)))) &&
  (quote.availableCreditMicros === undefined ||
    (Number.isSafeInteger(quote.availableCreditMicros) && quote.availableCreditMicros >= 0)) &&
  (quote.agreedMaximumSecondsDefault === undefined ||
    (Number.isSafeInteger(quote.agreedMaximumSecondsDefault) &&
      quote.agreedMaximumSecondsDefault >= 15 &&
      quote.agreedMaximumSecondsDefault <= LIVE_MAXIMUM_SECONDS));
/** The voices the person may choose from this quote; the pilot default when the World publishes none. */
export const liveVoiceChoices = (quote: LiveQuote | null): readonly string[] =>
  quote?.voices?.choices ?? [quote?.voice ?? LIVE_DEFAULT_VOICE];

/** One controller survives composer unmounts so an unknown admission cannot become a new call. */
export class LiveTalkController {
  private scope: LiveScope | null = null;
  private generation = 0;
  private attempt: Attempt | null = null;
  private privacyChange: {
    scope: LiveScope;
    requestKey: string;
    expectedRevision: number;
    taskContext: LivePrivacy["taskContext"];
  } | null = null;
  private voiceChange: {
    scope: LiveScope;
    requestKey: string;
    /** The voice choice's own revision, read from the World before the first save; null until read. */
    expectedRevision: number | null;
    voice: string;
  } | null = null;
  private agreedChosen = false;
  private agreedDefaultAdopted = false;
  private listeners = new Set<() => void>();
  private conversationListeners = new Set<(event: LiveConversationEvent) => void>();
  state: LiveState = {
    phase: "off",
    quote: null,
    call: null,
    error: null,
    privacySaving: false,
    privacyRetry: false,
    privacyPending: null,
    voiceSaving: false,
    voiceRetry: false,
    voicePending: null,
    agreedMaximumSeconds: LIVE_INITIAL_SEGMENT_SECONDS,
    fundedSeconds: null,
    deadlineAt: null,
    extensions: 0,
    extensionRefusal: null,
    refusal: null,
    replacementOf: null,
    device: "released",
  };
  private readonly deps: LiveDependencies;
  constructor(deps: LiveDependencies) {
    this.deps = deps;
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  snapshot = () => this.state;
  ownAgentCall = (worldKey: string, actorId: string, agentKey: string) => {
    const attempt = this.attempt;
    return attempt &&
      attempt.scope.worldKey === worldKey &&
      attempt.scope.expectedActorId === actorId &&
      attempt.scope.agent?.agentKey === agentKey
      ? {
          requestKey: attempt.requestKey,
          revision: attempt.scope.agent.revision,
          mode: attempt.scope.agent.mode,
        }
      : null;
  };
  subscribeConversation = (listener: (event: LiveConversationEvent) => void) => {
    this.conversationListeners.add(listener);
    return () => {
      this.conversationListeners.delete(listener);
    };
  };
  isCurrentCall = (requestKey: string) =>
    this.attempt?.requestKey === requestKey &&
    !this.attempt.cancelled &&
    identity(this.attempt.scope) === identity(this.scope) &&
    this.state.phase === "live";
  /** The request key of the connected call, when one is live. */
  currentCallKey = () =>
    this.attempt && this.isCurrentCall(this.attempt.requestKey) ? this.attempt.requestKey : null;
  sendUpdate = (requestKey: string, update: LiveUpdate) => {
    if (!this.isCurrentCall(requestKey) || !this.attempt?.media.sendUpdate)
      throw new Error("This Live connection is no longer available for task updates.");
    this.attempt.media.sendUpdate(update);
  };
  private update(patch: Partial<LiveState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((f) => f());
  }
  private updateAttempt(attempt: Attempt, patch: Partial<LiveState>) {
    const visible = identity(attempt.scope) === identity(this.scope);
    this.update(
      visible
        ? patch
        : {
            ...patch,
            call: null,
            quote: null,
            error: patch.error
              ? "A previous Live call is still being reconciled. Retry End if needed."
              : null,
          },
    );
  }
  private emit(attempt: Attempt, event: LiveConversationEvent) {
    if (!this.isCurrentCall(attempt.requestKey)) return;
    this.conversationListeners.forEach((listener) => listener(event));
  }
  private closeMedia(attempt: Attempt) {
    attempt.media.close();
    this.deps.clock.clearTimeout(attempt.timer);
    this.deps.clock.clearTimeout(attempt.deadline);
    this.deps.clock.clearTimeout(attempt.extendTimer);
    this.update({ device: attempt.media.released?.() ? "released" : "held" });
  }
  setScope(scope: LiveScope | null) {
    if (identity(scope) === identity(this.scope)) return;
    this.generation++;
    this.scope = scope ? jsonClone(scope) : null;
    this.privacyChange = null;
    this.voiceChange = null;
    this.agreedDefaultAdopted = false;
    this.update({
      privacySaving: false,
      privacyRetry: false,
      privacyPending: null,
      voiceSaving: false,
      voiceRetry: false,
      voicePending: null,
      refusal: null,
      extensionRefusal: null,
      replacementOf: null,
    });
    if (this.attempt) {
      this.update({ quote: null, call: null, error: null });
      this.end();
    } else this.update({ phase: "off", quote: null, call: null, error: null });
  }
  canReview = (address: Pick<LiveScope, "worldKey" | "expectedActorId" | "conversation">) =>
    !!this.scope &&
    !this.attempt &&
    this.scope.worldKey === address.worldKey &&
    this.scope.expectedActorId === address.expectedActorId &&
    Object.entries(address.conversation).every(
      ([key, value]) => this.scope!.conversation[key as keyof LiveScope["conversation"]] === value,
    );
  /** The person's agreed limit for this conversation; re-quotes so the maximum charge is the World's number. */
  setAgreedMaximumSeconds = async (seconds: number) => {
    if (!this.scope || this.attempt || !LIVE_LIMIT_CHOICES.includes(seconds)) return;
    this.agreedChosen = true;
    if (seconds === this.state.agreedMaximumSeconds) return;
    this.update({ agreedMaximumSeconds: seconds });
    if (this.state.phase === "quoted" || this.state.phase === "quoting")
      await this.quote(this.scope.agent);
  };
  quote = async (agent?: LiveAgentReference) => {
    if (
      !this.scope ||
      this.attempt ||
      this.state.privacySaving ||
      this.privacyChange ||
      this.state.voiceSaving ||
      this.voiceChange
    )
      return;
    if (agent !== undefined && !validLiveAgentReference(agent)) {
      this.update({
        phase: "error",
        quote: null,
        call: null,
        error: "Choose a saved voice agent revision to test.",
      });
      return;
    }
    const { agent: _previous, ...base } = this.scope;
    this.scope = agent ? { ...base, agent: liveAgentReference(agent) } : base;
    const generation = ++this.generation;
    const scope = this.scope;
    this.update({ phase: "quoting", quote: null, call: null, error: null });
    try {
      const result = (await this.deps.request("quote", {
        ...requestScope(scope),
        maximumSeconds: this.state.agreedMaximumSeconds,
        ...(scope.agent ? { agent: scope.agent } : {}),
      })) as LiveQuote;
      if (generation !== this.generation) return;
      if (
        !matches(scope, result) ||
        !matchesLiveAgent(scope.agent, result.agent) ||
        !validLivePrivacy(result.privacy) ||
        !result.price?.revision ||
        result.price.currency !== "USD" ||
        !Number.isFinite(result.price.rateMicrosPerMinute) ||
        result.price.rateMicrosPerMinute < 0 ||
        !Number.isFinite(result.maximumVoiceChargeMicros) ||
        result.maximumVoiceChargeMicros < 0 ||
        !validQuoteExtras(result)
      )
        throw new Error("The Live quote did not match this conversation.");
      // Adopt the World's suggested limit once, unless the person already chose.
      const suggested = result.agreedMaximumSecondsDefault;
      if (
        !this.agreedChosen &&
        !this.agreedDefaultAdopted &&
        suggested !== undefined &&
        LIVE_LIMIT_CHOICES.includes(suggested) &&
        suggested !== this.state.agreedMaximumSeconds
      ) {
        this.agreedDefaultAdopted = true;
        this.update({ agreedMaximumSeconds: suggested, quote: result, phase: "quoted" });
        await this.quote(scope.agent);
        return;
      }
      this.agreedDefaultAdopted = true;
      this.update({ phase: "quoted", quote: result });
    } catch (error) {
      if (generation === this.generation) this.update({ phase: "error", error: message(error) });
    }
  };
  setPrivacy = async (taskContext: LivePrivacy["taskContext"]) => {
    if (
      !this.scope ||
      this.attempt ||
      this.state.privacySaving ||
      this.state.voiceSaving ||
      this.voiceChange ||
      !["spoken-request-only", "supporting-dialogue"].includes(taskContext)
    )
      return;
    if (!this.privacyChange) {
      if (this.state.phase !== "quoted" || !this.state.quote) return;
      this.privacyChange = {
        scope: jsonClone(this.scope),
        requestKey: this.deps.uuid(),
        expectedRevision: this.state.quote.privacy.revision,
        taskContext,
      };
    }
    const change = this.privacyChange;
    if (change.taskContext !== taskContext || identity(change.scope) !== identity(this.scope))
      return;
    const generation = ++this.generation;
    this.update({
      privacySaving: true,
      privacyRetry: false,
      privacyPending: change.taskContext,
      error: null,
    });
    try {
      const saved = (await this.deps.request("privacy-set", {
        ...requestScope(change.scope),
        requestKey: change.requestKey,
        expectedRevision: change.expectedRevision,
        taskContext: change.taskContext,
      })) as LivePrivacyRead;
      if (generation !== this.generation) return;
      if (
        saved.ok !== true ||
        saved.worldKey !== change.scope.worldKey ||
        saved.actorId !== change.scope.expectedActorId ||
        !validLivePrivacy(saved.privacy) ||
        saved.privacy.revision !== change.expectedRevision + 1 ||
        saved.privacy.taskContext !== change.taskContext
      )
        throw new Error("The privacy receipt did not match this choice.");
      const current = (await this.deps.request(
        "privacy",
        requestScope(change.scope),
      )) as LivePrivacyRead;
      if (generation !== this.generation) return;
      if (
        current.ok !== true ||
        current.worldKey !== change.scope.worldKey ||
        current.actorId !== change.scope.expectedActorId ||
        !validLivePrivacy(current.privacy) ||
        current.privacy.revision < saved.privacy.revision ||
        (current.privacy.revision === saved.privacy.revision &&
          current.privacy.taskContext !== saved.privacy.taskContext)
      )
        throw new Error("The current privacy choice could not be confirmed.");
      this.privacyChange = null;
      this.update({
        phase: "quoting",
        privacySaving: false,
        privacyRetry: false,
        privacyPending: null,
      });
      await this.quote(change.scope.agent);
    } catch (error) {
      if (generation === this.generation)
        this.update({
          privacySaving: false,
          privacyRetry: true,
          error: `${message(error)} Retry this same privacy request before starting Live.`,
        });
    }
  };
  retryPrivacy = () =>
    this.privacyChange ? this.setPrivacy(this.privacyChange.taskContext) : Promise.resolve();
  reviewPrivacy = async () => {
    if (!this.scope || this.attempt || this.state.privacySaving || this.state.voiceSaving) return;
    const agent = this.scope.agent;
    this.privacyChange = null;
    this.voiceChange = null;
    this.update({
      phase: "quoting",
      quote: null,
      error: null,
      privacySaving: false,
      privacyRetry: false,
      privacyPending: null,
      voiceSaving: false,
      voiceRetry: false,
      voicePending: null,
    });
    await this.quote(agent);
  };
  /**
   * Save the chosen voice through the World's privacy record (voice-set), then
   * re-quote so the next start passes exactly the saved choice. An uncertain
   * save retries the same request key; it never silently starts a call.
   */
  setVoice = async (voice: string) => {
    if (
      !this.scope ||
      this.attempt ||
      this.state.voiceSaving ||
      this.state.privacySaving ||
      this.privacyChange ||
      !validLiveVoiceName(voice)
    )
      return;
    if (!this.voiceChange) {
      if (this.state.phase !== "quoted" || !this.state.quote) return;
      if (!liveVoiceChoices(this.state.quote).includes(voice)) {
        this.update({ error: "Choose a voice the World publishes for this conversation." });
        return;
      }
      if ((this.state.quote.voice ?? LIVE_DEFAULT_VOICE) === voice) return;
      this.voiceChange = {
        scope: jsonClone(this.scope),
        requestKey: this.deps.uuid(),
        expectedRevision: null,
        voice,
      };
    }
    const change = this.voiceChange;
    if (change.voice !== voice || identity(change.scope) !== identity(this.scope)) return;
    const generation = ++this.generation;
    this.update({ voiceSaving: true, voiceRetry: false, voicePending: change.voice, error: null });
    try {
      if (change.expectedRevision === null) {
        // The voice choice carries its own revision; the quote's privacy revision
        // is a different record. Read the current choice once, then save against it.
        const current = (await this.deps.request(
          "privacy",
          requestScope(change.scope),
        )) as LivePrivacyRead;
        if (generation !== this.generation) return;
        if (
          current.ok !== true ||
          (current.voice !== undefined && !validLiveVoiceChoice(current.voice))
        )
          throw new Error("The current voice choice could not be read.");
        change.expectedRevision = current.voice?.revision ?? 0;
      }
      const expectedRevision = change.expectedRevision;
      const saved = (await this.deps.request("voice-set", {
        ...requestScope(change.scope),
        requestKey: change.requestKey,
        expectedRevision,
        voice: change.voice,
      })) as LivePrivacyRead;
      if (generation !== this.generation) return;
      if (
        saved.ok !== true ||
        saved.worldKey !== change.scope.worldKey ||
        saved.actorId !== change.scope.expectedActorId ||
        !validLivePrivacy(saved.privacy) ||
        !validLiveVoiceChoice(saved.voice) ||
        saved.voice.revision !== expectedRevision + 1 ||
        saved.voice.voice !== change.voice
      )
        throw new Error("The voice receipt did not match this choice.");
      this.voiceChange = null;
      this.update({ phase: "quoting", voiceSaving: false, voiceRetry: false, voicePending: null });
      await this.quote(change.scope.agent);
      if (generation + 1 !== this.generation) return;
      if (this.state.phase === "quoted" && this.state.quote?.voice !== change.voice)
        this.update({
          phase: "error",
          error: "The saved voice did not read back from the World. Review the voice choice again.",
        });
    } catch (error) {
      if (generation === this.generation)
        this.update({
          voiceSaving: false,
          voiceRetry: true,
          error: `${message(error)} Retry this same voice request before starting Live.`,
        });
    }
  };
  retryVoice = () => (this.voiceChange ? this.setVoice(this.voiceChange.voice) : Promise.resolve());
  start = async (options?: { replacementOf: string; priorDialogue: LivePriorDialogueRow[] }) => {
    if (
      !this.scope ||
      this.attempt ||
      this.state.privacySaving ||
      this.privacyChange ||
      this.state.voiceSaving ||
      this.voiceChange ||
      this.state.phase !== "quoted" ||
      !this.state.quote
    )
      return;
    const scope = jsonClone(this.scope);
    const quote = this.state.quote;
    let media: LiveMedia;
    try {
      media = this.deps.media(
        () => {
          if (this.attempt !== attempt || attempt.cancelled) return;
          this.update({ phase: "live" });
          if (attempt.replacementOf)
            this.emit(attempt, {
              kind: "replacement",
              previousRequestKey: attempt.replacementOf,
              scope: attempt.scope,
              privacy: attempt.privacy,
              requestKey: attempt.requestKey,
            });
        },
        (error, detail) => {
          if (this.attempt !== attempt) return;
          if (detail?.reason === "connection-lost" && this.recover(attempt)) return;
          if (error) this.updateAttempt(attempt, { error });
          this.end();
        },
        {
          onTranscript: (fragment) => {
            if (!this.isCurrentCall(attempt.requestKey)) return;
            this.remember(attempt, fragment);
            this.emit(attempt, {
              kind: "transcript",
              fragment,
              scope: attempt.scope,
              privacy: attempt.privacy,
              requestKey: attempt.requestKey,
            });
          },
          onDelegation: (delegation) => {
            this.emit(attempt, {
              kind: "delegation",
              delegation,
              scope: attempt.scope,
              privacy: attempt.privacy,
              requestKey: attempt.requestKey,
            });
          },
        },
      );
    } catch (error) {
      this.update({ phase: "error", error: message(error) });
      return;
    }
    const agreed = this.state.agreedMaximumSeconds;
    const attempt: Attempt = {
      priceRevision: quote.price.revision,
      privacy: Object.freeze(jsonClone(quote.privacy)),
      scope,
      ...(quote.agent ? { agent: jsonClone(quote.agent) } : {}),
      requestKey: this.deps.uuid(),
      voice: quote.agent?.voice ?? quote.voice ?? LIVE_DEFAULT_VOICE,
      agreed,
      maximumSeconds: Math.min(LIVE_INITIAL_SEGMENT_SECONDS, agreed),
      cancelled: false,
      sent: false,
      startPending: false,
      media,
      answerApplied: false,
      reconcileUntil: this.deps.clock.now() + 150_000,
      failures: 0,
      deadlineAt: null,
      extensions: 0,
      extending: false,
      extensionRefused: false,
      dropped: false,
      replacementOf: options?.replacementOf ?? null,
      dialogue: [],
      dialogueBytes: 0,
    };
    this.attempt = attempt;
    this.updateAttempt(attempt, {
      phase: "starting",
      error: null,
      refusal: null,
      extensionRefusal: null,
      fundedSeconds: attempt.maximumSeconds,
      deadlineAt: null,
      extensions: 0,
      replacementOf: attempt.replacementOf,
      device: "held",
    });
    // A local bound only until the World's expiry is read back; the readback replaces it.
    attempt.deadline = this.deps.clock.setTimeout(this.end, attempt.maximumSeconds * 1000);
    try {
      const sdpOffer = await attempt.media.offer();
      if (attempt.cancelled) return;
      if (!sdpOffer || sdpOffer.length > 65_536)
        throw new Error("Live could not prepare its audio connection.");
      attempt.sent = true;
      attempt.startPending = true;
      const result = (await this.deps.request("start", {
        ...scope,
        requestKey: attempt.requestKey,
        maximumSeconds: attempt.maximumSeconds,
        priceRevision: quote.price.revision,
        privacyRevision: quote.privacy.revision,
        voice: attempt.voice,
        sdpOffer,
        ...(options?.priorDialogue.length ? { priorDialogue: options.priorDialogue } : {}),
      })) as LiveRead;
      attempt.startPending = false;
      if (attempt.cancelled) {
        // A stop that read null before admission is not a cancellation receipt.
        await this.reconcile(attempt, true);
        return;
      }
      await this.accept(attempt, result);
      this.schedule(attempt);
    } catch (error) {
      attempt.startPending = false;
      if (this.attempt !== attempt) return;
      if (error instanceof LiveStartNotDispatched && error.requestKey === attempt.requestKey) {
        attempt.cancelled = true;
        attempt.dropped = false;
        this.finish(attempt, null);
        this.updateAttempt(attempt, {
          phase: "error",
          error: message(error),
          refusal: message(error),
        });
        return;
      }
      this.updateAttempt(attempt, { error: message(error) });
      this.end();
    } finally {
      if (attempt.cancelled && !attempt.sent && this.attempt === attempt)
        this.finish(attempt, null);
    }
  };
  private remember(attempt: Attempt, fragment: LiveTranscriptFragment) {
    const bytes = utf8ByteLength(fragment.text);
    attempt.dialogue.push(fragment);
    attempt.dialogueBytes += bytes;
    while (
      attempt.dialogue.length > DIALOGUE_MAX_FRAGMENTS ||
      attempt.dialogueBytes > DIALOGUE_MAX_BYTES
    ) {
      const dropped = attempt.dialogue.shift()!;
      attempt.dialogueBytes -= utf8ByteLength(dropped.text);
    }
  }
  private async accept(attempt: Attempt, result: LiveRead) {
    if (this.attempt !== attempt) return;
    if (
      !matches(attempt.scope, result) ||
      (result.call &&
        (result.call.requestKey !== attempt.requestKey ||
          !validLivePrivacy(result.call.privacy) ||
          Object.entries(attempt.privacy).some(
            ([key, value]) => result.call!.privacy?.[key as keyof LivePrivacy] !== value,
          ) ||
          !matchesLiveAgent(attempt.scope.agent, result.call.agent) ||
          Object.entries(attempt.agent ?? {}).some(
            ([key, value]) => result.call!.agent?.[key as keyof LiveAgentSnapshot] !== value,
          ) ||
          result.call.backend.instanceId !== attempt.scope.backend.instanceId ||
          result.call.backend.model !== attempt.scope.backend.model ||
          result.call.voice !== attempt.voice))
    )
      throw new Error("Live readback did not match the original call.");
    if (result.admission !== undefined || result.refusalReceipt !== undefined) {
      const receipt = result.refusalReceipt;
      if (
        result.call !== null ||
        result.admission !== "not-dispatched" ||
        result.requestKey !== attempt.requestKey ||
        receipt?.kind !== "live-admission-refusal-v1" ||
        receipt.requestKey !== attempt.requestKey ||
        !Number.isSafeInteger(receipt.createdAt) ||
        receipt.createdAt < 0
      )
        throw new Error("Live refusal did not match the original admission request.");
      attempt.cancelled = true;
      attempt.dropped = false;
      this.finish(attempt, null);
      const reason = result.reason ?? "The World refused Live admission.";
      this.updateAttempt(attempt, { phase: "error", error: reason, refusal: reason });
      return;
    }
    const refusal = liveRefusalText(result.call);
    if (refusal) this.updateAttempt(attempt, { refusal });
    if (terminal(result.call)) {
      this.finish(attempt, result.call);
      return;
    }
    if (
      !attempt.cancelled &&
      (result.call?.status === "unconfirmed" || result.call?.status === "stopping")
    ) {
      this.updateAttempt(attempt, { call: result.call });
      this.end();
      return;
    }
    if (result.call) this.adoptBounds(attempt, result.call);
    this.updateAttempt(attempt, {
      call: result.call,
      phase: attempt.cancelled
        ? attempt.dropped
          ? "reconnecting"
          : "stopping"
        : result.call?.status === "unconfirmed"
          ? "unconfirmed"
          : this.state.phase === "live"
            ? "live"
            : "connecting",
      fundedSeconds: attempt.maximumSeconds,
      deadlineAt: attempt.deadlineAt,
      extensions: attempt.extensions,
    });
    if (!attempt.cancelled && result.call?.transportAnswer && !attempt.answerApplied) {
      attempt.answerApplied = true;
      await attempt.media.answer(
        result.call.transportAnswer.sdp,
        result.call.transportAnswer.providerSessionId,
      );
    }
    if (!attempt.cancelled) this.scheduleExtension(attempt);
  }
  /** The World's expiry and funded total read back own the deadline; a local timer never does once they exist. */
  private adoptBounds(attempt: Attempt, call: LiveCall) {
    if (Number.isSafeInteger(call.maximumSeconds) && call.maximumSeconds! > 0)
      attempt.maximumSeconds = call.maximumSeconds!;
    if (Number.isSafeInteger(call.extensions) && call.extensions! >= 0)
      attempt.extensions = call.extensions!;
    if (Number.isSafeInteger(call.expiresAt)) {
      const deadlineAt = call.expiresAt! - LIVE_WORLD_CLOSE_MARGIN_MS;
      if (deadlineAt !== attempt.deadlineAt) {
        attempt.deadlineAt = deadlineAt;
        // The reconciliation window runs from the read-back deadline, so a
        // funded call longer than the window is never called unconfirmed while live.
        attempt.reconcileUntil = Math.max(attempt.reconcileUntil, deadlineAt + RECONCILE_WINDOW_MS);
        this.deps.clock.clearTimeout(attempt.deadline);
        if (!attempt.cancelled)
          attempt.deadline = this.deps.clock.setTimeout(
            this.end,
            Math.max(0, deadlineAt - this.deps.clock.now()),
          );
      }
    }
  }
  /** Ask for the next funded segment shortly before the deadline while the agreed limit allows. */
  private scheduleExtension(attempt: Attempt) {
    this.deps.clock.clearTimeout(attempt.extendTimer);
    if (
      this.attempt !== attempt ||
      attempt.cancelled ||
      attempt.extending ||
      attempt.extensionRefused ||
      attempt.deadlineAt === null ||
      attempt.maximumSeconds >= Math.min(attempt.agreed, LIVE_MAXIMUM_SECONDS)
    )
      return;
    attempt.extendTimer = this.deps.clock.setTimeout(
      () => {
        void this.extend(attempt);
      },
      Math.max(0, attempt.deadlineAt - LIVE_EXTENSION_LEAD_MS - this.deps.clock.now()),
    );
  }
  private async extend(attempt: Attempt) {
    if (this.attempt !== attempt || attempt.cancelled || attempt.extending) return;
    const target = Math.min(
      attempt.maximumSeconds + LIVE_EXTENSION_STEP_SECONDS,
      attempt.agreed,
      LIVE_MAXIMUM_SECONDS,
    );
    if (target <= attempt.maximumSeconds) return;
    attempt.extending = true;
    try {
      const result = (await this.deps.request("extend", {
        ...requestScope(attempt.scope),
        requestKey: attempt.requestKey,
        maximumSeconds: target,
      })) as LiveRead;
      if (this.attempt !== attempt) return;
      attempt.extending = false;
      if (
        !result.call ||
        !Number.isSafeInteger(result.call.maximumSeconds) ||
        result.call.maximumSeconds! < target ||
        !Number.isSafeInteger(result.call.expiresAt)
      )
        throw new Error("The World did not confirm the extension.");
      await this.accept(attempt, result);
    } catch (error) {
      if (this.attempt !== attempt) return;
      attempt.extending = false;
      attempt.extensionRefused = true;
      this.updateAttempt(attempt, {
        extensionRefusal: `${message(error)} Live ends at its current funded limit.`,
      });
    }
  }
  /**
   * The audio connection was lost while live and the person did not End:
   * keep the dialogue, End the dropped call through the stop door, wait for
   * its terminal read, then start exactly one replacement carrying the
   * dialogue as memory. A replacement never replaces itself; further
   * recovery is the person's.
   */
  private recover(attempt: Attempt): boolean {
    if (this.attempt !== attempt || attempt.cancelled || attempt.replacementOf !== null)
      return false;
    if (!attempt.answerApplied || this.state.phase !== "live") return false;
    const remaining =
      attempt.deadlineAt === null ? Infinity : attempt.deadlineAt - this.deps.clock.now();
    const extensible = attempt.maximumSeconds < Math.min(attempt.agreed, LIVE_MAXIMUM_SECONDS);
    if (remaining < RECOVERY_MINIMUM_REMAINING_MS && !extensible) return false;
    this.generation++;
    attempt.cancelled = true;
    attempt.dropped = true;
    this.closeMedia(attempt);
    attempt.reconcileUntil = Math.max(attempt.reconcileUntil, this.deps.clock.now() + 45_000);
    this.updateAttempt(attempt, { phase: "reconnecting", error: null });
    if (attempt.sent) void this.reconcile(attempt, true);
    else this.finish(attempt, null);
    return true;
  }
  private async replace(previous: Attempt) {
    const scope = this.scope;
    if (!scope || this.attempt || identity(previous.scope) !== identity(scope)) {
      this.update({ phase: "ended", replacementOf: null });
      return;
    }
    await this.quote(scope.agent);
    // Can 68: Live's price is a dated record. If it changed while the call
    // dropped, the replacement waits for the person to start it at the new
    // price; it never continues at a price they did not review.
    const requoted = this.state.phase === "quoted" ? this.state.quote : null;
    if (
      requoted &&
      requoted.price.revision !== previous.priceRevision &&
      identity(this.scope) === identity(scope)
    ) {
      this.update({
        phase: "error",
        error: `Live dropped, and its price has changed to ${(requoted.price.rateMicrosPerMinute / 1_000_000).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 })} a minute. Start Live again to continue at the new price.`,
      });
      return;
    }
    if (
      this.state.phase !== "quoted" ||
      this.attempt ||
      identity(this.scope) !== identity(scope) ||
      this.state.agreedMaximumSeconds !== previous.agreed
    ) {
      if (this.state.phase !== "error" && identity(this.scope) === identity(scope))
        this.update({
          phase: "error",
          error:
            this.state.error ??
            "Live dropped and could not be re-quoted. Start Live again when you are ready.",
        });
      return;
    }
    // The exact quote this recovery reviewed against the person's price; nothing that replaced it while the
    // transcript was saved (a new limit, a scope left and re-entered, a new price) is ever started unasked.
    const reviewed = this.state.quote;
    let flushed = false;
    if (this.deps.flushTranscript) {
      try {
        flushed = await this.deps.flushTranscript(previous.requestKey);
      } catch {
        flushed = false;
      }
      if (
        this.state.phase !== "quoted" ||
        this.attempt ||
        identity(this.scope) !== identity(scope) ||
        this.state.quote !== reviewed ||
        reviewed?.price.revision !== previous.priceRevision ||
        this.state.agreedMaximumSeconds !== previous.agreed
      )
        return;
    }
    await this.start({
      replacementOf: previous.requestKey,
      priorDialogue: flushed ? [] : livePriorDialogueRows(previous.dialogue),
    });
  }
  private finish(attempt: Attempt, call: LiveCall | null) {
    this.closeMedia(attempt);
    if (this.attempt !== attempt) return;
    this.attempt = null;
    const refusal = liveRefusalText(call);
    const replace =
      attempt.dropped &&
      terminal(call) &&
      !refusal &&
      identity(attempt.scope) === identity(this.scope);
    this.updateAttempt(attempt, {
      phase: replace ? "reconnecting" : "ended",
      call,
      quote: null,
      ...(refusal ? { refusal } : {}),
    });
    if (replace) void this.replace(attempt);
  }
  private schedule(attempt: Attempt) {
    this.deps.clock.clearTimeout(attempt.timer);
    if (this.attempt !== attempt) return;
    if (this.deps.clock.now() >= attempt.reconcileUntil) {
      this.closeMedia(attempt);
      this.updateAttempt(attempt, {
        phase: "unconfirmed",
        error:
          "Microphone stopped. Final call status and charge are not yet confirmed. Retry End to reconcile this call.",
      });
      return;
    }
    attempt.timer = this.deps.clock.setTimeout(
      () => {
        void this.reconcile(attempt, attempt.cancelled);
      },
      Math.min(10_000, 2000 * 2 ** attempt.failures),
    );
  }
  private async reconcile(attempt: Attempt, stop: boolean) {
    if (this.attempt !== attempt) return;
    try {
      const result = (await this.deps.request(stop ? "stop" : "read", {
        ...requestScope(attempt.scope),
        requestKey: attempt.requestKey,
      })) as LiveRead;
      if (this.attempt !== attempt) return;
      await this.accept(attempt, result);
      attempt.failures = 0;
      // Null is never terminal once a start crossed the network: its outcome may still arrive.
    } catch (error) {
      if (this.attempt !== attempt) return;
      attempt.failures++;
      this.updateAttempt(attempt, { phase: "unconfirmed", error: message(error) });
      if (!attempt.cancelled) {
        this.end();
        return;
      }
    }
    this.schedule(attempt);
  }
  end = () => {
    this.generation++;
    const attempt = this.attempt;
    if (!attempt) {
      this.privacyChange = null;
      this.voiceChange = null;
      this.update({
        phase: "off",
        quote: null,
        error: null,
        refusal: null,
        extensionRefusal: null,
        replacementOf: null,
        privacySaving: false,
        privacyRetry: false,
        privacyPending: null,
        voiceSaving: false,
        voiceRetry: false,
        voicePending: null,
      });
      return;
    }
    attempt.cancelled = true;
    // The person's End wins over any pending automatic recovery.
    attempt.dropped = false;
    this.closeMedia(attempt);
    attempt.reconcileUntil = Math.max(attempt.reconcileUntil, this.deps.clock.now() + 45_000);
    this.update({ phase: "stopping" });
    if (attempt.sent) void this.reconcile(attempt, true);
    else this.finish(attempt, null);
  };
  /**
   * End and wait, bounded, for the stop round trip to settle (sign-out,
   * World switch). Resolves "idle" when nothing was running, "ended" when the
   * call reached its terminal read within the bound, else "unconfirmed".
   */
  endAndSettle = (boundMs = 8_000): Promise<"idle" | "ended" | "unconfirmed"> => {
    if (!this.attempt) {
      this.end();
      return Promise.resolve("idle");
    }
    this.end();
    if (!this.attempt) return Promise.resolve("ended");
    return new Promise((resolve) => {
      let settled = false;
      const done = (outcome: "ended" | "unconfirmed") => {
        if (settled) return;
        settled = true;
        unsubscribe();
        this.deps.clock.clearTimeout(timer);
        resolve(outcome);
      };
      const unsubscribe = this.subscribe(() => {
        if (!this.attempt) done("ended");
      });
      const timer = this.deps.clock.setTimeout(() => done("unconfirmed"), boundMs);
    });
  };
}
